import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarClock, Eye, Send, Trash2, RefreshCw, Sparkles, Loader2, Hash, Pencil, X } from "lucide-react";
import api, { formatApiError, API } from "@/lib/api";
import { ensureDLToken } from "@/lib/dlToken";

/**
 * Admin Invoices → Weekly Drafts panel.
 *
 * Renders every un-released draft the Monday scheduler prepared (one per
 * pharmacy that had delivered orders in the prior ISO week). Each row has
 * three actions:
 *   - View (open PDF preview in a new tab, short-lived download token)
 *   - Send (email PDF to billing_email + flip to issued)
 *   - Discard (leaves a gap in the sequence — intentional per the spec)
 *
 * A "Run now" button (super_admin) triggers the same worker manually — used
 * for catching up after a missed Monday or previewing this week's drafts.
 */
const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (!isNaN(d.getTime())) return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {}
  return iso;
};

export default function WeeklyDraftsPanel({ onDraftReleased }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [discardTarget, setDiscardTarget] = useState(null);
  const [nextNumber, setNextNumber] = useState("");
  const [seqDialogOpen, setSeqDialogOpen] = useState(false);
  const [seqInput, setSeqInput] = useState("");
  const [seqSaving, setSeqSaving] = useState(false);
  // Draft editor state — the ✎ pencil icon on each row opens this.
  const [editTarget, setEditTarget] = useState(null); // draft row being edited
  const [editRows, setEditRows] = useState([]);       // line items from GET /rows
  const [editForm, setEditForm] = useState({ invoice_number: "", billing_email: "", period_from: "", period_to: "", line_prices: {} });
  const [editLoading, setEditLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [drafts, nextNum] = await Promise.all([
        api.get("/admin/invoices/drafts"),
        api.get("/admin/invoices/next-number").catch(() => ({ data: { invoice_number: "" } })),
      ]);
      setRows(drafts.data || []);
      setNextNumber(nextNum.data?.invoice_number || "");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/invoices/drafts/run-now");
      const c = (data.created || []).length;
      const s = (data.skipped || []).length;
      toast.success(`Weekly drafts run — created ${c}${s ? `, skipped ${s} already-existing` : ""}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRunning(false);
    }
  };

  const view = async (row) => {
    try {
      const token = await ensureDLToken();
      window.open(`${API}/admin/invoices/drafts/${row.id}/pdf?auth=${encodeURIComponent(token)}`, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const send = async (row) => {
    if (!window.confirm(`Send invoice ${row.invoice_number} to ${row.billing_email || "(pharmacy billing email)"}?`)) return;
    setBusyId(row.id);
    try {
      const { data } = await api.post(`/admin/invoices/drafts/${row.id}/send`, {});
      toast.success(`Invoice ${data.invoice_number} sent to ${data.to}`);
      await load();
      onDraftReleased?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const openSeqDialog = () => {
    // Prefill with the current next-number so a small tweak is a one-key change.
    const digits = (nextNumber || "").replace(/^\D+/, "") || "1";
    setSeqInput(digits);
    setSeqDialogOpen(true);
  };

  const saveNextNumber = async () => {
    const n = parseInt(seqInput, 10);
    if (!Number.isFinite(n) || n < 1) {
      toast.error("Enter a positive number, e.g. 82");
      return;
    }
    setSeqSaving(true);
    try {
      const { data } = await api.post("/admin/invoices/next-number", { next_number: n });
      setNextNumber(data.next_number);
      toast.success(`Next invoice will be ${data.next_number}`);
      setSeqDialogOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSeqSaving(false);
    }
  };

  const doDiscard = async () => {
    if (!discardTarget) return;
    setBusyId(discardTarget.id);
    try {
      const { data } = await api.delete(`/admin/invoices/drafts/${discardTarget.id}`);
      toast.success(`Draft ${data.invoice_number} discarded — a gap will remain in the sequence.`);
      setDiscardTarget(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const openEditor = async (row) => {
    setEditTarget(row);
    setEditLoading(true);
    setEditRows([]);
    setEditForm({
      invoice_number: row.invoice_number || "",
      billing_email: row.billing_email || "",
      period_from: row.period_from || "",
      period_to: row.period_to || "",
      line_prices: {},
    });
    try {
      const { data } = await api.get(`/admin/invoices/drafts/${row.id}/rows`);
      const rows = data.rows || [];
      setEditRows(rows);
      // Seed the per-line price map with current (possibly overridden) values.
      const prices = {};
      for (const r of rows) prices[r.order_id] = r.price;
      setEditForm((prev) => ({ ...prev, line_prices: prices }));
    } catch (e) {
      toast.error(formatApiError(e));
      setEditTarget(null);
    } finally {
      setEditLoading(false);
    }
  };

  const setLinePrice = (order_id, value) => {
    setEditForm((prev) => ({ ...prev, line_prices: { ...prev.line_prices, [order_id]: value } }));
  };

  const editTotal = React.useMemo(() => {
    return editRows.reduce((sum, r) => {
      const v = editForm.line_prices[r.order_id];
      const n = typeof v === "number" ? v : parseFloat(v);
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0);
  }, [editRows, editForm.line_prices]);

  const saveEditor = async () => {
    if (!editTarget) return;
    // Only send overrides for lines whose price actually differs from the
    // original computed price (rows[i].price). Sending nulls for the rest
    // is unnecessary — the backend merges, so unchanged keys stay.
    const price_overrides = {};
    for (const r of editRows) {
      const raw = editForm.line_prices[r.order_id];
      const n = typeof raw === "number" ? raw : parseFloat(raw);
      if (Number.isFinite(n) && Math.abs(n - r.price) > 0.001) {
        price_overrides[r.order_id] = Number(n.toFixed(2));
      }
    }
    const payload = { replace_price_overrides: true, price_overrides };
    if (editForm.invoice_number && editForm.invoice_number.trim() !== (editTarget.invoice_number || "").trim()) {
      payload.invoice_number = editForm.invoice_number.trim();
    }
    if ((editForm.billing_email || "") !== (editTarget.billing_email || "")) {
      payload.billing_email = editForm.billing_email || null;
    }
    if (editForm.period_from && editForm.period_from !== editTarget.period_from) payload.period_from = editForm.period_from;
    if (editForm.period_to && editForm.period_to !== editTarget.period_to) payload.period_to = editForm.period_to;

    setEditSaving(true);
    try {
      await api.patch(`/admin/invoices/drafts/${editTarget.id}`, payload);
      toast.success("Draft updated");
      setEditTarget(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <Card className="rounded-xl border-border mb-5 overflow-hidden" data-testid="weekly-drafts-panel">
      <div className="px-5 py-4 border-b border-border bg-secondary/30 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full grid place-items-center bg-primary/10 text-primary shrink-0">
            <CalendarClock className="h-4 w-4" />
          </div>
          <div>
            <h2 className="text-base font-semibold tracking-tight">Weekly Drafts</h2>
            <p className="text-[11px] text-muted-foreground">
              Auto-prepped every Monday · one draft per pharmacy with deliveries in the prior week · numbers reserved on creation
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {nextNumber && (
            <button
              type="button"
              onClick={openSeqDialog}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary/40 hover:bg-secondary px-2.5 py-1 text-[11px] font-mono-num transition-colors"
              title="Set the next invoice number (e.g. after migrating from another system)"
              data-testid="weekly-drafts-next-number"
            >
              <Hash className="h-3 w-3 opacity-60" />
              <span className="opacity-70">Next:</span>
              <span className="font-semibold">{nextNumber}</span>
              <Pencil className="h-3 w-3 opacity-40" />
            </button>
          )}
          <Button
            variant="outline" size="sm" className="rounded-full"
            onClick={load} disabled={loading || running}
            data-testid="weekly-drafts-refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button
            size="sm" className="rounded-full"
            onClick={runNow} disabled={running}
            data-testid="weekly-drafts-run-now"
          >
            {running ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
            {running ? "Preparing…" : "Run weekly prep now"}
          </Button>
        </div>
      </div>

      {rows.length === 0 && !loading ? (
        <div className="px-5 py-8 text-center text-sm text-muted-foreground" data-testid="weekly-drafts-empty">
          No drafts waiting. The next run happens automatically every Monday at 6 AM ET.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-5 py-2.5 font-semibold">Invoice #</th>
                <th className="text-left px-3 py-2.5 font-semibold">Pharmacy</th>
                <th className="text-left px-3 py-2.5 font-semibold">Period</th>
                <th className="text-right px-3 py-2.5 font-semibold">Orders</th>
                <th className="text-right px-3 py-2.5 font-semibold">Amount</th>
                <th className="text-left px-3 py-2.5 font-semibold">Billing email</th>
                <th className="text-right px-5 py-2.5 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isBusy = busyId === r.id;
                return (
                  <tr key={r.id} className="border-t border-border hover:bg-secondary/20 transition-colors" data-testid={`draft-row-${r.invoice_number}`}>
                    <td className="px-5 py-2.5 font-mono-num font-semibold text-primary whitespace-nowrap">{r.invoice_number}</td>
                    <td className="px-3 py-2.5">{r.pharmacy_name || "—"}</td>
                    <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">
                      {fmtDate(r.period_from)} <span className="opacity-50">→</span> {fmtDate(r.period_to)}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono-num">{r.rows ?? 0}</td>
                    <td className="px-3 py-2.5 text-right font-mono-num font-semibold">{fmtMoney(r.total)}</td>
                    <td className="px-3 py-2.5 text-muted-foreground truncate max-w-[220px]" title={r.billing_email || "(not set)"}>{r.billing_email || <span className="italic">not set</span>}</td>
                    <td className="px-5 py-2.5 whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm" variant="ghost" className="h-8 w-8 p-0 rounded-full text-primary hover:bg-primary/10"
                          title="Preview PDF"
                          onClick={() => view(r)} disabled={isBusy}
                          data-testid={`draft-view-${r.invoice_number}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm" variant="ghost" className="h-8 w-8 p-0 rounded-full text-slate-700 hover:bg-slate-100"
                          title="Edit invoice details (invoice #, billing email, line prices)"
                          onClick={() => openEditor(r)} disabled={isBusy}
                          data-testid={`draft-edit-${r.invoice_number}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm" variant="ghost" className="h-8 w-8 p-0 rounded-full text-emerald-700 hover:bg-emerald-100"
                          title={r.billing_email ? `Send to ${r.billing_email}` : "Set a billing_email in Manage → Pharmacies first"}
                          onClick={() => send(r)} disabled={isBusy || !r.billing_email}
                          data-testid={`draft-send-${r.invoice_number}`}
                        >
                          {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        </Button>
                        <Button
                          size="sm" variant="ghost" className="h-8 w-8 p-0 rounded-full text-destructive hover:bg-destructive/10"
                          title="Discard draft (leaves a gap in the sequence)"
                          onClick={() => setDiscardTarget(r)} disabled={isBusy}
                          data-testid={`draft-discard-${r.invoice_number}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="max-w-3xl" data-testid="draft-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit draft {editTarget?.invoice_number}</DialogTitle>
            <DialogDescription>
              Change any field before you release this invoice to the pharmacy.
              Changes here don't affect other drafts and can be reverted by editing again.
            </DialogDescription>
          </DialogHeader>

          {editLoading ? (
            <div className="py-8 text-center text-muted-foreground text-sm flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading line items…
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Invoice #</label>
                  <input
                    type="text"
                    value={editForm.invoice_number}
                    onChange={(e) => setEditForm((p) => ({ ...p, invoice_number: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 font-mono-num text-sm"
                    data-testid="draft-edit-invoice-number"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Billing email</label>
                  <input
                    type="email"
                    value={editForm.billing_email}
                    onChange={(e) => setEditForm((p) => ({ ...p, billing_email: e.target.value }))}
                    placeholder="billing@pharmacy.com"
                    className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                    data-testid="draft-edit-billing-email"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Period from</label>
                  <input
                    type="date"
                    value={editForm.period_from}
                    onChange={(e) => setEditForm((p) => ({ ...p, period_from: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                    data-testid="draft-edit-period-from"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Period to</label>
                  <input
                    type="date"
                    value={editForm.period_to}
                    onChange={(e) => setEditForm((p) => ({ ...p, period_to: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                    data-testid="draft-edit-period-to"
                  />
                </div>
              </div>

              <div className="border border-border rounded-xl overflow-hidden">
                <div className="px-3 py-2 bg-secondary/40 flex items-center justify-between">
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Line items</div>
                  <div className="text-xs">
                    <span className="text-muted-foreground">Live total: </span>
                    <span className="font-mono-num font-semibold">{fmtMoney(editTotal)}</span>
                  </div>
                </div>
                <div className="max-h-[300px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="text-[11px] uppercase tracking-wider text-muted-foreground bg-secondary/20">
                      <tr>
                        <th className="text-left px-3 py-1.5 font-semibold">Order #</th>
                        <th className="text-left px-3 py-1.5 font-semibold">Patient</th>
                        <th className="text-right px-3 py-1.5 font-semibold">Miles</th>
                        <th className="text-right px-3 py-1.5 font-semibold w-[130px]">Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editRows.map((r) => (
                        <tr key={r.order_id} className="border-t border-border">
                          <td className="px-3 py-1.5 font-mono-num text-xs whitespace-nowrap">{r.order_number || r.order_id?.slice(0, 8)}</td>
                          <td className="px-3 py-1.5">{r.patient_name || "—"}</td>
                          <td className="px-3 py-1.5 text-right font-mono-num text-xs text-muted-foreground">{Number(r.miles || 0).toFixed(2)}</td>
                          <td className="px-3 py-1.5">
                            <div className="relative">
                              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>
                              <input
                                type="number" step="0.01" min="0"
                                value={editForm.line_prices[r.order_id] ?? ""}
                                onChange={(e) => setLinePrice(r.order_id, e.target.value)}
                                className="w-full rounded-md border border-border bg-background pl-5 pr-2 py-1 text-right font-mono-num text-sm"
                                data-testid={`draft-edit-price-${r.order_id}`}
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                      {editRows.length === 0 && (
                        <tr><td colSpan={4} className="px-3 py-4 text-center text-muted-foreground">No line items in this period.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditTarget(null)}>Cancel</Button>
            <Button onClick={saveEditor} disabled={editSaving || editLoading} data-testid="draft-edit-save">
              {editSaving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={seqDialogOpen} onOpenChange={setSeqDialogOpen}>
        <DialogContent data-testid="set-next-number-dialog">
          <DialogHeader>
            <DialogTitle>Set the next invoice number</DialogTitle>
            <DialogDescription>
              After you save, the very next invoice (auto-draft OR manual) will be numbered exactly this value.
              Useful when migrating from another system — e.g. type <code className="font-mono">82</code> so the next release is <code className="font-mono">SRCI-0082</code>.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 mt-1">
            <span className="font-mono-num text-sm text-muted-foreground">SRCI-</span>
            <input
              type="number"
              min="1"
              value={seqInput}
              onChange={(e) => setSeqInput(e.target.value)}
              className="flex-1 rounded-lg border border-border bg-background px-3 py-2 font-mono-num text-sm"
              placeholder="82"
              autoFocus
              data-testid="set-next-number-input"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSeqDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveNextNumber} disabled={seqSaving} data-testid="set-next-number-save">
              {seqSaving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!discardTarget} onOpenChange={(o) => !o && setDiscardTarget(null)}>
        <DialogContent data-testid="draft-discard-dialog">
          <DialogHeader>
            <DialogTitle>Discard draft {discardTarget?.invoice_number}?</DialogTitle>
            <DialogDescription>
              This draft was prepared automatically for <strong>{discardTarget?.pharmacy_name}</strong>.
              Discarding it will <strong>permanently leave {discardTarget?.invoice_number} as a gap</strong>
              in the invoice sequence — the next draft (or manual invoice) will use the following number.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDiscardTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={doDiscard} data-testid="draft-discard-confirm">
              Discard draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
